import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, HStack, Text, Button, IconButton, Flex,
} from '@chakra-ui/react';
import { CloseIcon, WarningIcon } from '@chakra-ui/icons';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import CreditTopUpModal from './CreditTopUpModal';
import { useLocation } from 'react-router-dom';

const SESSION_KEY = 'lowCreditBannerDismissed';
const HIDDEN_PATHS = ['/login', '/register', '/reset-password', '/update-password', '/admin', '/docs'];

const LowCreditBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  const [credits, setCredits] = useState(null);
  const [planType, setPlanType] = useState(null);
  const [hasSavedPaymentMethod, setHasSavedPaymentMethod] = useState(false);
  const [resetAt, setResetAt] = useState(null);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const location = useLocation();

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/subscription/status');
      if (res.ok) {
        const data = await res.json();
        setCredits(data.credits || null);
        setPlanType(data.planType || null);
        setHasSavedPaymentMethod(data.hasSavedPaymentMethod || false);
        setResetAt(data.credits?.resetAt || null);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === 'true') {
      setDismissed(true);
    }
    // Only fetch when a token is present — fetchWithAuth hard-navigates to
    // /login on missing token, which would cause an infinite redirect loop
    // because this banner mounts on every page (above <Routes>).
    const token = localStorage.getItem('token');
    if (token) {
      fetchStatus();
    }
  }, [fetchStatus, location.pathname]);

  const isHiddenPath = HIDDEN_PATHS.some(p => location.pathname.startsWith(p));

  if (!credits || dismissed || isHiddenPath) return null;

  const { total, allowance } = credits;
  const threshold = allowance * 0.20;

  if (total > threshold || allowance === 0) return null;

  const resetLabel = resetAt
    ? new Date(resetAt).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })
    : null;

  const isExhausted = total === 0;

  const message = isExhausted
    ? planType === 'pro'
      ? `Your AI team has clocked out. Top up to get them back to work${resetLabel ? `, or try again on ${resetLabel} when your credits reset` : ''}.`
      : `Your AI team has clocked out. Top up or upgrade to Pro to continue${resetLabel ? `, or try again on ${resetLabel} when your credits reset` : ''}.`
    : planType === 'pro'
      ? `Your AI team is about to clock out. Top up your credits to keep them working${resetLabel ? ` — resets ${resetLabel}` : ''}.`
      : `Your AI team is about to clock out. Top up your credits or upgrade to Pro${resetLabel ? ` — resets ${resetLabel}` : ''}.`;

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setDismissed(true);
  };

  return (
    <>
      <Box
        bg="orange.50"
        borderBottom="1px"
        borderColor="orange.200"
        px={4}
        py={2}
        position="sticky"
        top={0}
        zIndex={999}
      >
        <Flex
          align="center"
          justify="center"
          maxW="1200px"
          mx="auto"
          position="relative"
          minH="28px"
          px={{ base: 8, md: 12 }}
        >
          <HStack
            spacing={3}
            justify="center"
            align="center"
            flexWrap="wrap"
            textAlign="center"
          >
            <HStack spacing={2} align="center" minW={0}>
              <WarningIcon color="orange.500" boxSize={4} flexShrink={0} />
              <Text fontSize="sm" color="orange.800" fontWeight="600">
                {message}
              </Text>
              {!isExhausted && (
                <Text fontSize="xs" color="orange.600" whiteSpace="nowrap">
                  ({total} credit{total !== 1 ? 's' : ''} remaining)
                </Text>
              )}
            </HStack>
            <Button
              size="xs"
              colorScheme="orange"
              variant="solid"
              onClick={() => setIsTopUpOpen(true)}
              flexShrink={0}
            >
              Top Up
            </Button>
          </HStack>
          <IconButton
            icon={<CloseIcon />}
            size="xs"
            variant="ghost"
            color="orange.500"
            aria-label="Dismiss banner"
            onClick={handleDismiss}
            position="absolute"
            right={{ base: 0, md: 2 }}
            top="50%"
            transform="translateY(-50%)"
          />
        </Flex>
      </Box>

      <CreditTopUpModal
        isOpen={isTopUpOpen}
        onClose={() => { setIsTopUpOpen(false); fetchStatus(); }}
        hasSavedPaymentMethod={hasSavedPaymentMethod}
        planType={planType}
        resetAt={resetAt}
      />
    </>
  );
};

export default LowCreditBanner;
